import React, { useState, useRef, useEffect } from 'react';
import {
  Home, FileText, Mail, FolderOpen, Sliders, ChevronLeft, ChevronRight,
  ChevronDown, Sparkles, Settings, LogOut, Sun, Moon, Plus, Upload
} from 'lucide-react';

// Refactored modules imports
import NavItem from './AppSidebar/navigation';
import CreateMenu from './AppSidebar/actions';
import { LibraryMenu, CoverLetterMenu } from './AppSidebar/library';

export default function AppSidebar({
  activeTab,
  onTabChange,
  atsScore,
  credits = 0,
  user,
  variants = [],
  activeVariantId = null,
  onSelectVariant,
  onDeleteVariant,
  onCreateVariant,
  theme = 'light',
  toggleTheme,
  onLogout,
  onClearChat,
  onImportResume,
  onOpenCustomize,
  onUpgrade,
  coverLetters = [],
}) {

  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('rz_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const effectiveCollapsed = isCollapsed && !isMobile;

  const [brandHovered, setBrandHovered] = useState(false);
  const [resumesExpanded, setResumesExpanded] = useState(true);
  const [coversExpanded, setCoversExpanded] = useState(true);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const profileRef = useRef(null);

  // Lifted create-modal state
  const [createModalStep, setCreateModalStep] = useState(null);
  const openCreateModal = (step = 'select') => setCreateModalStep(step);
  const closeCreateModal = () => setCreateModalStep(null);

  const isDark = theme === 'dark';
  const sidebarBg = isDark ? '#0A0A0C' : '#FFFFFF';
  const sidebarBorder = isDark ? '#1F1F24' : '#E5E7EB';
  const textPrimary = isDark ? '#F9FAFB' : '#111827';
  const textSecondary = isDark ? '#9CA3AF' : '#4B5563';

  useEffect(() => {
    function handleClickOutside(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem('rz_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };




  return (
    <aside
      style={{
        width: effectiveCollapsed ? '72px' : '230px',
        minWidth: isMobile ? 'auto' : (effectiveCollapsed ? '72px' : '230px'),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: sidebarBg,
        borderRight: `1px solid ${sidebarBorder}`,
        overflow: 'visible',
        position: 'relative',
        zIndex: 30,
        flexShrink: 0,
        fontFamily: "'Inter', sans-serif",
        transition: 'width 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        boxSizing: 'border-box',
      }}
    >
      {/* Branding Section */}
      <div
        onMouseEnter={() => setBrandHovered(true)}
        onMouseLeave={() => setBrandHovered(false)}
        style={{
          height: '64px',
          minHeight: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: effectiveCollapsed ? 'center' : 'space-between',
          padding: effectiveCollapsed ? '0' : '0 20px',
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        <div
          onClick={effectiveCollapsed ? handleToggleCollapse : undefined}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: effectiveCollapsed ? 'pointer' : 'default' }}
        >
           <img
            src="/resumebar-icon.svg"
            alt="Resumebar Icon"
            style={{ width: '28px', height: '28px', objectFit: 'contain' }}
          />
          {!effectiveCollapsed && (
            <span
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: '17px',
                fontWeight: 800,
                color: isDark ? '#ffffff' : '#14213D',
                letterSpacing: '-0.5px',
                lineHeight: '1.2'
              }}
            >
              resumebar
            </span>
          )}
        </div>

        {!effectiveCollapsed && !isMobile && (
          <button
            onClick={handleToggleCollapse}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: textSecondary,
              opacity: brandHovered ? 1 : 0,
              transition: 'opacity 0.15s ease, background-color 0.15s ease',
              outline: 'none',
              padding: 0,
            }}
            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <ChevronLeft size={15} />
          </button>
        )}
      </div>

      {/* ── CREATE NEW ACTION BUTTON — opens wizard directly, no dropdown ── */}
      <div style={{ padding: effectiveCollapsed ? '4px 0 12px' : '0 16px 12px', display: 'flex', justifyContent: 'flex-start' }}>
        {!effectiveCollapsed ? (
          <button
            onClick={() => openCreateModal('select')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '8px' : '12px',
              width: '100%',
              height: isMobile ? '38px' : '42px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--brand)',
              color: '#FFFFFF',
              fontWeight: 700,
              fontSize: isMobile ? '12px' : '13px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(14, 165, 233, 0.25)',
              transition: 'all 0.15s ease',
              outline: 'none',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--brand-hover)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.35)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'var(--brand)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(14, 165, 233, 0.25)';
            }}
          >
            <Plus size={15} strokeWidth={2.5} />
            <span>Create New</span>
          </button>
        ) : (
          <button
            onClick={() => openCreateModal('select')}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              border: 'none',
              background: 'var(--brand)',
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(14, 165, 233, 0.2)',
              outline: 'none'
            }}
          >
            <Plus size={16} strokeWidth={2.5} />
          </button>
        )}
      </div>

      {/* User profile dropdown trigger hidden inside top profile card trigger - triggered relative to profileCompleteness (hidden/replaced) */}
      <div ref={profileRef} style={{ position: 'relative' }}>
        {profileMenuOpen && (
          <div
            style={{
              position: 'absolute',
              top: '10px',
              left: '16px',
              right: '16px',
              borderRadius: '8px',
              background: isDark ? '#18181B' : '#FFFFFF',
              border: `1px solid ${sidebarBorder}`,
              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
              padding: '4px',
              zIndex: 300,
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            {/* Theme Toggle */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleTheme?.();
                setProfileMenuOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '8px 10px',
                borderRadius: '6px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '12.5px',
                color: textPrimary,
                textAlign: 'left',
                fontWeight: 500,
                outline: 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {isDark ? <Sun size={14} /> : <Moon size={14} />}
              <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
            </button>

            <div style={{ height: '1px', background: sidebarBorder, margin: '4px 0' }} />

            {/* Sign Out */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onLogout?.();
                setProfileMenuOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '8px 10px',
                borderRadius: '6px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '12.5px',
                color: '#EF4444',
                textAlign: 'left',
                fontWeight: 500,
                outline: 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <LogOut size={14} />
              <span>Sign Out</span>
            </button>
          </div>
        )}
      </div>

      {/* Main Navigation Area */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}>

        {/* Navigation Items */}
        <NavItem
          icon={Home}
          label="Dashboard"
          onClick={() => onTabChange('home')}
          active={activeTab === 'home'}
          isCollapsed={effectiveCollapsed}
          theme={theme}
        />

        {/* Resumes Section Trigger Button */}
        {!effectiveCollapsed ? (
          <button
            onClick={() => setResumesExpanded(!resumesExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              height: '40px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0 16px',
              fontFamily: "'Inter', sans-serif",
              fontSize: '13px',
              fontWeight: 500,
              color: textSecondary,
              outline: 'none',
              textAlign: 'left',
              borderRadius: '6px',
              marginTop: '4px',
            }}
            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FileText size={18} style={{ color: textSecondary, flexShrink: 0 }} />
              <span style={{ color: textPrimary, fontWeight: 500 }}>Resumes</span>
            </div>
            <ChevronDown
              size={13}
              style={{
                transform: resumesExpanded ? 'none' : 'rotate(-90deg)',
                transition: 'transform 0.15s ease',
                opacity: 0.6,
                color: textPrimary
              }}
            />
          </button>
        ) : (
          <NavItem
            icon={FileText}
            label="Resumes"
            active={['chat', 'editor', 'ats', 'jd', 'templates'].includes(activeTab) && activeVariantId}
            onClick={() => {
              setIsCollapsed(false);
              setResumesExpanded(true);
            }}
            isCollapsed={effectiveCollapsed}
            theme={theme}
          />
        )}

        {/* Resumes Library Menu Sub-List */}
        <LibraryMenu
          isCollapsed={effectiveCollapsed}
          libraryExpanded={resumesExpanded}
          theme={theme}
          openCreateModal={openCreateModal}
          isMobile={isMobile}
        />

        {/* Cover Letters Section Trigger Button */}
        {!effectiveCollapsed ? (
          <button
            onClick={() => setCoversExpanded(!coversExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              height: '40px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0 16px',
              fontFamily: "'Inter', sans-serif",
              fontSize: '13px',
              fontWeight: 500,
              color: textSecondary,
              outline: 'none',
              textAlign: 'left',
              borderRadius: '6px',
              marginTop: '4px',
            }}
            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Mail size={18} style={{ color: textSecondary, flexShrink: 0 }} />
              <span style={{ color: textPrimary, fontWeight: 500 }}>Cover Letters</span>
            </div>
            <ChevronDown
              size={13}
              style={{
                transform: coversExpanded ? 'none' : 'rotate(-90deg)',
                transition: 'transform 0.15s ease',
                opacity: 0.6,
                color: textPrimary
              }}
            />
          </button>
        ) : (
          <NavItem
            icon={Mail}
            label="Cover Letters"
            active={activeTab === 'cover'}
            onClick={() => {
              setIsCollapsed(false);
              setCoversExpanded(true);
            }}
            isCollapsed={effectiveCollapsed}
            theme={theme}
          />
        )}

        {/* Cover Letters Menu Sub-List */}
        <CoverLetterMenu
          isCollapsed={effectiveCollapsed}
          coverExpanded={coversExpanded}
          theme={theme}
          isMobile={isMobile}
        />

        {/* Career Vault */}
        <NavItem
          icon={FolderOpen}
          label="Career Vault"
          onClick={() => onTabChange('assets_vault')}
          active={activeTab === 'assets_vault'}
          isCollapsed={effectiveCollapsed}
          theme={theme}
        />

        <div style={{ height: '1px', background: sidebarBorder, margin: '8px 0' }} />

        {/* Account Settings */}
        <NavItem
          icon={Settings}
          label="Account Settings"
          onClick={() => onTabChange('profile')}
          active={activeTab === 'profile'}
          isCollapsed={effectiveCollapsed}
          theme={theme}
        />
      </div>

      {/* Footer Section */}
      <div
        style={{
          marginTop: 'auto',
          borderTop: `1px solid ${sidebarBorder}`,
          padding: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          background: isDark ? '#0A0A0C' : '#FAF9FC',
          position: 'relative',
        }}
      >
        {/* Credits Widget */}
        {!effectiveCollapsed ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 10px',
              borderRadius: '6px',
              background: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
              fontSize: '11px',
              color: textSecondary,
              fontFamily: 'Inter, sans-serif',
              border: `1px dashed ${isDark ? '#2D2D35' : '#E5E7EB'}`,
              marginBottom: '2px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10B981' }} />
              <span>AI Credits</span>
            </div>
            <strong style={{ color: textPrimary }}>
              {credits} remaining
            </strong>
          </div>
        ) : (
          <div
            title={`${credits} AI credits remaining`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'center',
              width: '32px',
              height: '20px',
              borderRadius: '10px',
              background: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
              fontSize: '10px',
              fontWeight: 800,
              color: textSecondary,
              border: `1px dashed ${isDark ? '#2D2D35' : '#E5E7EB'}`,
              marginBottom: '2px',
            }}
          >
            {credits}
          </div>
        )}

        {/* Upgrade Button — BUG-S04 fix: was setting activeTab='pricing' which has no render case */}
        {!effectiveCollapsed ? (
          <button
            onClick={() => onUpgrade?.()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '8px' : '12px',
              width: '100%',
              height: isMobile ? '36px' : '40px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--brand)',
              color: '#FFFFFF',
              fontWeight: 700,
              fontSize: isMobile ? '11px' : '12px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(14, 165, 233, 0.25)',
              transition: 'all 0.15s ease',
              outline: 'none',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--brand-hover)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.35)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'var(--brand)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(14, 165, 233, 0.25)';
            }}
          >
            <Sparkles size={13} fill="white" />
            <span>Upgrade</span>
          </button>
        ) : (
          <button
            onClick={() => onUpgrade?.()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: 'none',
              background: 'var(--brand)',
              color: '#FFFFFF',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(14, 165, 233, 0.2)',
              transition: 'all 0.15s ease',
              outline: 'none',
              alignSelf: 'center'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--brand-hover)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'var(--brand)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <Sparkles size={16} fill="white" />
          </button>
        )}
      </div>

      {/* Floating Toggle Button */}
      {!isMobile && (
        <button
          onClick={handleToggleCollapse}
          aria-label={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          style={{
            position: 'absolute',
            right: '-9px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '18px',
            height: '24px',
            borderRadius: '12px',
            border: `1px solid ${sidebarBorder}`,
            background: isDark ? '#1F1F24' : '#FFFFFF',
            color: textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isDark
              ? '0 4px 10px rgba(0, 0, 0, 0.45), 0 2px 4px rgba(0, 0, 0, 0.3)'
              : '0 4px 10px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
            zIndex: 100,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            outline: 'none',
            padding: 0,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = isDark ? '#2D2D35' : '#F3F4F6';
            e.currentTarget.style.color = 'var(--brand)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.08)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = isDark ? '#1F1F24' : '#FFFFFF';
            e.currentTarget.style.color = textSecondary;
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          }}
        >
          {isCollapsed ? (
            <ChevronRight size={12} strokeWidth={2.5} />
          ) : (
            <ChevronLeft size={12} strokeWidth={2.5} />
          )}
        </button>
      )}

      {/* ── stepper actions creation modal ── */}
      <CreateMenu
        externalStep={createModalStep}
        onExternalClose={closeCreateModal}
        onCreateVariant={onCreateVariant}
        onImportResume={onImportResume}
        theme={theme}
      />
    </aside>
  );
}
